import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { Warning, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';

export default function KlantenViesPanel() {
  const [inst, setInst] = useState({});
  const [busy, setBusy] = useState(false);
  const [checkBusy, setCheckBusy] = useState(false);

  const load = () => API.get('/instellingen').then(r => setInst(r.data || {}));
  useEffect(() => { load(); }, []);

  const saveToggle = async (v) => {
    setBusy(true);
    try {
      await API.put('/instellingen', { auto_klant_check_enabled: v });
      setInst(p => ({ ...p, auto_klant_check_enabled: v }));
    } finally { setBusy(false); }
  };

  const checkNu = async () => {
    setCheckBusy(true);
    try {
      const r = await API.post('/klanten/check-alle');
      alert(`${r.data.gecontroleerd} klanten gecontroleerd — ${r.data.wijzigingen} wijziging${r.data.wijzigingen === 1 ? '' : 'en'} gevonden.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Controle mislukt');
    } finally {
      setCheckBusy(false);
    }
  };

  const globalAan = inst.auto_klant_check_enabled !== false;

  return (
    <div className="space-y-6" data-testid="klanten-vies-panel">
      <div>
        <h2 className="text-xl font-bold text-[#0A0A0A] tracking-tight">Klanten & VIES controle</h2>
        <p className="text-sm text-gray-500 mt-1">Automatische wekelijkse VIES-check van alle klanten met een BTW-nummer. Wijzigingen in naam, adres of actief-status genereren automatisch een notificatie.</p>
      </div>

      {/* Global toggle */}
      <div className="bg-white border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <ArrowsClockwise size={16} className="text-[#002FA7]" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Automatische controle</span>
        </div>
        <div className="p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={globalAan}
              onChange={e => saveToggle(e.target.checked)}
              disabled={busy}
              className="mt-1"
              data-testid="toggle-auto-check"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Wekelijkse automatische controle inschakelen</div>
              <div className="text-xs text-gray-500 mt-0.5">Elke maandag om 06:00u worden alle klanten met een BTW-nummer gecontroleerd via VIES. Bij wijzigingen krijg je een notificatie. Je kan deze controle ook per klant uitschakelen via de checkbox "Auto VIES-check" in de klant-detailpagina.</div>
            </div>
          </label>
        </div>
      </div>

      {/* Status & handmatige controle */}
      <div className="bg-white border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <CheckCircle size={16} className="text-[#008A27]" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Controle-status</span>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Laatste controle</div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                {inst.laatste_klant_check
                  ? new Date(inst.laatste_klant_check).toLocaleString('nl-BE', { dateStyle: 'long', timeStyle: 'short' })
                  : '— Nog niet uitgevoerd'}
              </div>
              {inst.laatste_klant_check_aantal && (
                <div className="text-xs text-gray-500 mt-0.5">{inst.laatste_klant_check_aantal} klant(en) gecontroleerd</div>
              )}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Volgende automatische controle</div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                {globalAan ? 'Eerstvolgende maandag 06:00u' : '— Uitgeschakeld'}
              </div>
            </div>
          </div>
          <div className="pt-3 border-t border-gray-100">
            <button
              className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
              onClick={checkNu}
              disabled={checkBusy}
              data-testid="inst-check-nu-btn"
            >
              <ArrowsClockwise size={14} weight="bold" className={checkBusy ? 'animate-spin' : ''} />
              {checkBusy ? 'Bezig met controleren…' : 'Controleer alle klanten nu'}
            </button>
            <span className="ml-3 text-xs text-gray-400">Dit kan even duren (1 klant = ~1-2s via VIES).</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50/50 border border-amber-200 p-4 text-xs text-amber-900 flex items-start gap-2">
        <Warning size={18} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
        <div>
          <div className="font-bold mb-1">Hoe werkt het?</div>
          <ul className="list-disc ml-4 space-y-0.5">
            <li>VIES (VAT Information Exchange System) is de officiële EU-dienst voor BTW-validatie</li>
            <li>De eerste controle na toevoeging van een klant dient als referentiewaarde</li>
            <li>Bij elk verschil wordt een notificatie aangemaakt + historie bijgehouden per klant</li>
            <li>Kritieke wijzigingen (klant niet meer actief bij BTW-admin) krijgen een rode markering</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
